import React, { useState, useRef, useEffect } from "react";

interface OTPInputProps {
	numDigits: number;
	onComplete: (otp: string) => void;
	disabled?: boolean;
}

const OTPInput: React.FC<OTPInputProps> = ({
	numDigits,
	onComplete,
	disabled,
}) => {
	const [otp, setOtp] = useState<string[]>(Array(numDigits).fill(""));
	const [error, setError] = useState(false);
	const inputRefs = useRef<Array<HTMLInputElement | null>>(
		Array(numDigits).fill(null)
	);

	// Reset OTP when disabled
	useEffect(() => {
		if (disabled) {
			setOtp(Array(numDigits).fill("")); // Clear state
			inputRefs.current.forEach((input) => {
				if (input) input.value = ""; // Clear input fields
			});
			onComplete("");
		}
	}, [disabled, numDigits]);

	// Handle individual input changes
	const handleChange = (index: number, value: string) => {
		if (!/^\d*$/.test(value)) return; // Restrict to numbers only

		const newOtp = [...otp];
		newOtp[index] = value;
		setOtp(newOtp);

		if (value && index < numDigits - 1) {
			inputRefs.current[index + 1]?.focus();
		}

		if (newOtp.join("").length === numDigits) {
			onComplete(newOtp.join(""));
		}
	};

	// Handle Backspace Navigation
	const handleKeyDown = (
		index: number,
		e: React.KeyboardEvent<HTMLInputElement>
	) => {
		if (e.key === "Backspace" && !otp[index] && index > 0) {
			inputRefs.current[index - 1]?.focus();
		}
	};

	// Handle Pasting OTP
	const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();
		const pastedData = e.clipboardData.getData("text").trim();

		if (/^\d+$/.test(pastedData) && pastedData.length === numDigits) {
			setOtp(pastedData.split(""));
			onComplete(pastedData);
		}
	};

	return (
		<div className="flex flex-col items-center">
			<div className="flex space-x-2">
				{otp.map((_, index) => (
					<input
						key={index}
						ref={(el) => (inputRefs.current[index] = el)}
						type="text"
						value={otp[index]}
						disabled={disabled}
						maxLength={1}
						className={`w-12 h-12 border text-center text-2xl font-code font-semibold rounded-md outline-none transition-all duration-200 ${
							error
								? "border-red-500 bg-red-100 focus:ring-2 focus:ring-red-500"
								: "border-gray-300 focus:ring-2 focus:ring-blue-500"
						}`}
						onChange={(e) => handleChange(index, e.target.value)}
						onKeyDown={(e) => handleKeyDown(index, e)}
						onPaste={handlePaste}
					/>
				))}
			</div>
			{error && <span className="text-red-500 mt-2">Invalid OTP</span>}
		</div>
	);
};

export default OTPInput;
